import { create } from 'zustand';
import { supabase } from '../../../supabase';
import * as api from '../api/feeApi';
import { periodKey } from '../helpers/format';

const CACHE_FRESH_MS = 30000;
const FAMILY_DEBOUNCE_MS = 600;
const RELOAD_DEBOUNCE_MS = 1500;
const SELF_WRITE_SKIP_MS = 2500;

export const EMPTY_STATS = Object.freeze({
  billed: 0, collected: 0, arrears: 0, outstanding: 0, advance: 0,
  families: 0, paid: 0, partial: 0, pending: 0,
});

// Mirrors the SQL definitions so a single-family patch keeps totals exact.
export function computeStats(families) {
  const s = { ...EMPTY_STATS };
  for (const f of families) {
    s.billed += Number(f.current) || 0;
    s.collected += Number(f.paid) || 0;
    s.arrears += Number(f.arrears) || 0;
    s.outstanding += Math.max(0, Number(f.outstanding) || 0);
    s.advance += Number(f.advance) || 0;
    if (f.challan_id) {
      s.families += 1;
      if (f.status === 'paid') s.paid += 1;
      else if (f.status === 'partial') s.partial += 1;
      else if (f.status === 'pending') s.pending += 1;
    }
  }
  for (const k of ['billed', 'collected', 'arrears', 'outstanding', 'advance']) s[k] = Math.round(s[k] * 100) / 100;
  return s;
}

const byDisplayId = (a, b) =>
  String(a.display_id || '').localeCompare(String(b.display_id || ''), undefined, { numeric: true });

function snapshotFrom(data) {
  return {
    month: data?.month ?? null,
    year: data?.year ?? null,
    period: data?.period || null,
    periods: Array.isArray(data?.periods) ? data.periods : [],
    families: Array.isArray(data?.families) ? data.families : [],
    loadedAt: Date.now(),
  };
}

function viewOf(snap) {
  const familyIndex = {};
  snap.families.forEach(f => { familyIndex[f.family_id] = f; });
  return {
    month: snap.month, year: snap.year, period: snap.period, periods: snap.periods,
    families: snap.families, familyIndex, stats: computeStats(snap.families), loadedAt: snap.loadedAt,
  };
}

const initialState = {
  month: null, year: null, period: null, periods: [],
  families: [], familyIndex: {}, stats: EMPTY_STATS,
  status: 'idle',            // idle | loading | refreshing | ready | error
  error: null, loadedAt: 0,
  cache: {},                 // periodKey → snapshot
  dues: {}, ledgers: {},     // familyId → payload
};

// Module-level bookkeeping — not UI state, never rendered.
let loadToken = 0;
let channel = null;
const loadInflight = new Map();   // periodKey → promise (StrictMode mounts twice)
let reloadTimer = null;
const familyTimers = new Map();
const familyInflight = new Map();
const selfWrites = new Map();

const dropKey = (obj, key) => {
  if (!obj || !(key in obj)) return obj;
  const next = { ...obj };
  delete next[key];
  return next;
};

export const useFeeStore = create((set, get) => ({
  ...initialState,

  // Load a period. No args → current or active period. Serves cache instantly, revalidates if stale.
  load: async ({ month = null, year = null, force = false } = {}) => {
    const s = get();
    const m = month ?? s.month;
    const y = year ?? s.year;
    const token = ++loadToken;

    if (m != null && y != null) {
      const cached = s.cache[periodKey(m, y)];
      if (cached && !force) {
        set({ ...viewOf(cached), status: 'ready', error: null });
        if (Date.now() - cached.loadedAt < CACHE_FRESH_MS) return cached;
        set({ status: 'refreshing' });
      } else {
        const same = s.month === m && s.year === y && s.families.length > 0;
        set(same
          ? { status: 'refreshing', error: null }
          : { month: m, year: y, families: [], familyIndex: {}, stats: EMPTY_STATS, status: 'loading', error: null });
      }
    } else {
      set({ status: s.families.length ? 'refreshing' : 'loading', error: null });
    }

    // One request per period even if two mounts ask at the same moment.
    const inflightKey = m != null && y != null ? periodKey(m, y) : 'active';
    const request = loadInflight.get(inflightKey)
      || api.getCollection({ month: m, year: y }).finally(() => loadInflight.delete(inflightKey));
    loadInflight.set(inflightKey, request);

    try {
      const data = await request;
      if (token !== loadToken) return null;
      const snap = snapshotFrom(data);
      set(st => ({
        ...viewOf(snap), status: 'ready', error: null,
        cache: { ...st.cache, [periodKey(snap.month, snap.year)]: snap },
      }));
      return snap;
    } catch (err) {
      if (token !== loadToken) return null;
      set({ status: get().families.length ? 'ready' : 'error', error: err.message });
      return null;
    }
  },

  // Re-fetch one family and patch it in place. Stats recompute locally.
  refreshFamily: async (familyId) => {
    if (!familyId) return null;
    if (familyInflight.has(familyId)) return familyInflight.get(familyId);
    const { month, year } = get();
    if (month == null || year == null) return null;

    const run = (async () => {
      try {
        const data = await api.getCollection({ month, year, familyId });
        const st = get();
        if (st.month !== month || st.year !== year) {
          // User switched period mid-flight: every cached period is now stale for this family.
          set({ cache: {}, dues: dropKey(st.dues, familyId), ledgers: dropKey(st.ledgers, familyId) });
          return null;
        }
        const key = periodKey(month, year);
        const row = (data?.families || [])[0] || null;
        let families = st.families.filter(f => f.family_id !== familyId);
        if (row) families = [...families, row].sort(byDisplayId);
        const snap = { month, year, period: data?.period ?? st.period, periods: st.periods, families, loadedAt: Date.now() };
        set({
          ...viewOf(snap),
          cache: { [key]: snap },          // other periods dropped — they hold stale balances
          dues: dropKey(st.dues, familyId),
          ledgers: dropKey(st.ledgers, familyId),
        });
        return row;
      } catch (err) {
        set({ error: err.message });
        return null;
      } finally {
        familyInflight.delete(familyId);
      }
    })();
    familyInflight.set(familyId, run);
    return run;
  },

  loadDues: async (familyId, { force = false } = {}) => {
    const cached = get().dues[familyId];
    if (cached && !force) return cached;
    const data = await api.getFamilyDues(familyId);
    set(st => ({ dues: { ...st.dues, [familyId]: data } }));
    return data;
  },

  loadLedger: async (familyId, { force = false } = {}) => {
    const cached = get().ledgers[familyId];
    if (cached && !force) return cached;
    const data = await api.getFamilyLedger(familyId);
    set(st => ({ ledgers: { ...st.ledgers, [familyId]: data } }));
    return data;
  },

  // ── Family-scoped writes: server call, then patch one row ──
  recordPayment: async (input) => {
    selfWrites.set(input.familyId, Date.now());
    const receipt = await api.recordPayment(input);
    await get().refreshFamily(input.familyId);
    return receipt;
  },
  reversePayment: async ({ familyId, paymentId, reason }) => {
    selfWrites.set(familyId, Date.now());
    await api.reversePayment({ paymentId, reason });
    await get().refreshFamily(familyId);
  },
  voidItems: async ({ familyId, itemIds, reason }) => {
    selfWrites.set(familyId, Date.now());
    const res = await api.voidItems({ itemIds, reason });
    await get().refreshFamily(familyId);
    return res;
  },
  waiveItems: async ({ familyId, waivers, reason }) => {
    selfWrites.set(familyId, Date.now());
    const res = await api.waiveItems({ waivers, reason });
    await get().refreshFamily(familyId);
    return res;
  },
  depositAdvance: async ({ familyId, ...rest }) => {
    selfWrites.set(familyId, Date.now());
    const res = await api.depositAdvance({ familyId, ...rest });
    await get().refreshFamily(familyId);
    return res;
  },

  // ── Period-scoped writes: server call, then full reload ──
  reloadAfterPeriodChange: async (month, year) => {
    set({ cache: {}, dues: {}, ledgers: {} });
    return get().load({ month, year, force: true });
  },
  generatePeriod: async (input) => {
    const res = await api.generatePeriod(input);
    await get().reloadAfterPeriodChange(input.month, input.year);
    return res;
  },
  discardDraft: async ({ month, year }) => {
    await api.discardDraft({ month, year });
    set({ month: null, year: null });
    await get().reloadAfterPeriodChange(null, null);
  },
  setPeriodStatus: async ({ month, year, status }) => {
    const res = await api.setPeriodStatus({ month, year, status });
    await get().reloadAfterPeriodChange(month, year);
    return res;
  },
  closePeriod: async ({ month, year }) => {
    await api.closePeriod({ month, year });
    await get().reloadAfterPeriodChange(month, year);
  },
  applyFines: async ({ month, year, amount, studentIds }) => {
    const res = await api.applyFines({ month, year, amount, studentIds });
    await get().reloadAfterPeriodChange(month, year);
    return res;
  },

  // ── Realtime: changes made by other cashiers ──
  subscribe: (schoolId) => {
    if (!schoolId) return;
    get().unsubscribe();
    const onFamilyChange = (payload) => {
      const fid = payload?.new?.family_id || payload?.old?.family_id;
      if (!fid) { get().scheduleReload(); return; }
      const mine = selfWrites.get(fid);
      if (mine && Date.now() - mine < SELF_WRITE_SKIP_MS) return;
      clearTimeout(familyTimers.get(fid));
      familyTimers.set(fid, setTimeout(() => {
        familyTimers.delete(fid);
        get().refreshFamily(fid);
      }, FAMILY_DEBOUNCE_MS));
    };
    const filter = `school_id=eq.${schoolId}`;
    channel = supabase
      .channel(`fees-v2-${schoolId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payments', filter }, onFamilyChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'advance_ledger', filter }, onFamilyChange)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'challan_items', filter }, () => get().scheduleReload())
      .subscribe();
  },
  scheduleReload: () => {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadTimer = null;
      const { month, year } = get();
      set({ cache: {} });
      get().load({ month, year, force: true });
    }, RELOAD_DEBOUNCE_MS);
  },
  unsubscribe: () => {
    clearTimeout(reloadTimer);
    reloadTimer = null;
    familyTimers.forEach(t => clearTimeout(t));
    familyTimers.clear();
    if (channel) { supabase.removeChannel(channel); channel = null; }
  },

  // Call on logout or school switch.
  reset: () => {
    get().unsubscribe();
    loadToken += 1;
    loadInflight.clear();
    familyInflight.clear();
    selfWrites.clear();
    set({ ...initialState });
  },
}));

export const selectFamily = (familyId) => (s) => s.familyIndex[familyId] || null;
export const selectStats = (s) => s.stats;
export const selectStatus = (s) => s.status;

export default useFeeStore;
